import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { TurbineHistorySeriesMetric } from './useTurbineHistorySeries';
import { turbinesKey } from '../../../solaris-lens-common-lib/src/constants/queryKeys/Turbines';
import {
  getTurbinesHistorySeries,
  getInstalledProduct,
  getHistoricalDataOptions,
  putAlarmsAcknowledge,
  getHistoryTags,
  exportSummaryHistory
} from '../../../solaris-lens-common-lib/src/services/installedProducts';
import {
  getTurbinesByLocationId,
  getTurbineDetailsServ,
  getTurbinesListServ,
  getActiveTurbinesListServ,
  createTurbineServ,
  updateTurbineServ,
  deleteTurbineServ
} from '../../../solaris-lens-common-lib/src/services/turbines';
import { RootState } from '../../../store';
import { TurbineResponse } from '../../../types/turbines';
import useShouldRefresh from '@/hooks/useShouldRefresh';
import { installedProductsKeys } from '@/solaris-lens-common-lib/src/constants/queryKeys/InstalledProducts';

type TurbinesListParams = Record<string, string>;

export interface TurbineFormData {
  id?: string;
  serialNumber: string;
  name: string;
  brand: string;
  model: string;
  turbineType: string;
  maxCapacity: string;
  manufacturingCountry: string;
  manufacturingYear: string;
  acquisitionYear: string;
  status: 'Active' | 'Inactive';
  isActive: boolean;
  dataPoints?: {
    genTotalKwAnalog: boolean;
    t5Temperature: boolean;
    ngp: boolean;
    percentLoad: boolean;
    voltage: boolean;
    frequency: boolean;
    powerFactor: boolean;
    gasPressure: boolean;
    t1Temperature: boolean;
  };
}

interface TurbineApiPayload {
  id: string;
  type: string;
  name: string;
  serialNumber: string;
  brand: string;
  subType: string;
  manufacturingCountry: string;
  manufacturingYear: string;
  maxCapacityMW: string;
  isActive: boolean;
  turbineType: string;
}

export const useGetTurbineList = (
  locationId: string,
  { useQueryOptions = {} } = {}
) => {
  const token = useSelector((state: RootState) => state?.auth?.accessToken);
  useShouldRefresh();

  return useQuery<TurbineResponse>({
    queryKey: turbinesKey.detail(token, locationId),
    queryFn: () => {
      return getTurbinesByLocationId(
        token,
        locationId
      ) as Promise<TurbineResponse>;
    },
    enabled: Boolean(token && locationId),
    ...useQueryOptions
  });
};

export const useGetTurbineDetails = (
  turbineId: string,
  { useQueryOptions = {} } = {}
) => {
  const token = useSelector((state: RootState) => state?.auth?.accessToken);
  useShouldRefresh();

  return useQuery({
    queryKey: turbinesKey.turbineDetail(token, turbineId),
    queryFn: () => {
      return getTurbineDetailsServ(token, turbineId);
    },
    enabled: Boolean(token && turbineId),
    ...useQueryOptions
  });
};

export const useGetTurbinesList = ({
  params,
  useQueryOptions = {}
}: {
  params?: TurbinesListParams;
  useQueryOptions?: Record<string, unknown>;
} = {}) => {
  const token = useSelector((state: RootState) => state?.auth?.accessToken);
  useShouldRefresh();

  return useQuery({
    queryKey: params
      ? [...turbinesKey.turbinesList(token), params]
      : turbinesKey.turbinesList(token),
    queryFn: () => {
      return getTurbinesListServ(token, params);
    },
    enabled: Boolean(token),
    meta: { errorDisplay: 'card' as const },
    ...useQueryOptions
  });
};

export const useGetActiveTurbinesList = ({ useQueryOptions = {} } = {}) => {
  const token = useSelector((state: RootState) => state?.auth?.accessToken);
  useShouldRefresh();

  return useQuery({
    queryKey: turbinesKey.turbinesList(token),
    queryFn: () => {
      return getActiveTurbinesListServ(token);
    },
    enabled: Boolean(token),
    meta: { errorDisplay: 'card' as const },
    ...useQueryOptions
  });
};

const transformFormDataToApiPayload = (
  formData: TurbineFormData
): TurbineApiPayload => {
  return {
    id: formData.id || formData.serialNumber,
    type: 'TURBINE',
    name: formData.name,
    serialNumber: formData.id || formData.serialNumber,
    brand: formData.brand,
    subType: formData.model,
    manufacturingCountry: formData.manufacturingCountry,
    manufacturingYear: formData.manufacturingYear,
    maxCapacityMW: formData.maxCapacity,
    isActive: formData.isActive,
    turbineType: formData.turbineType
  };
};

export const useCreateUpdateTurbine = (mode: 'create' | 'edit') => {
  const token = useSelector((state: RootState) => state?.auth?.accessToken);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (formData: TurbineFormData) => {
      const apiPayload = transformFormDataToApiPayload(formData);

      if (mode === 'edit' && formData.id) {
        return updateTurbineServ(token, formData.id, apiPayload);
      } else {
        return createTurbineServ(token, apiPayload);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: turbinesKey.all });
    },
    onError: error => {
      console.error('Error saving turbine:', error);
    }
  });
};

export const useDeleteTurbine = () => {
  const token = useSelector((state: RootState) => state?.auth?.accessToken);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (turbineId: string) => {
      return deleteTurbineServ(token, turbineId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: turbinesKey.all });
    },
    onError: error => {
      console.error('Error deleting turbine:', error);
    }
  });
};

export interface TurbinesHistorySeriesParams {
  startDate: string;
  endDate: string;
  category: string;
  metric: string;
  scope: string;
  interval: string;
  ids: string;
}

export const useGetTurbinesHistorySeries = (
  params: TurbinesHistorySeriesParams,
  { useQueryOptions = {} } = {}
) => {
  const token = useSelector((state: RootState) => state?.auth?.accessToken);
  useShouldRefresh();

  return useQuery({
    queryKey: turbinesKey.historySeries(token, params),
    queryFn: async () => {
      const data = await getTurbinesHistorySeries({ token, params });
      return data;
    },
    enabled: Boolean(token && params.ids),
    meta: { errorDisplay: 'toast' as const },
    ...useQueryOptions
  });
};

export const useGetTurbineSummary = (
  turbineId: string,
  { useQueryOptions = {} } = {}
) => {
  const token = useSelector((state: RootState) => state?.auth?.accessToken);
  useShouldRefresh();

  return useQuery({
    queryKey: turbinesKey.turbineSummary(token, turbineId),
    queryFn: () => {
      return getInstalledProduct(token, turbineId);
    },
    enabled: Boolean(token && turbineId),
    ...useQueryOptions
  });
};

export const useAlarmsAcknowledge = () => {
  const token = useSelector((state: RootState) => state?.auth?.accessToken);
  const name = useSelector((state: RootState) => state?.userProfile?.name);
  const queryClient = useQueryClient();
  useShouldRefresh();

  return useMutation({
    mutationFn: async ({
      turbineId,
      tag
    }: {
      turbineId: string;
      tag: string[];
    }) => {
      if (!token) {
        throw new Error('Token not available');
      }
      if (!name) {
        throw new Error('User name not available');
      }
      return putAlarmsAcknowledge({
        token,
        turbineId,
        params: { tag, ackBy: name }
      });
    },
    onSuccess: (_, variables) => {
      if (token) {
        queryClient.invalidateQueries({
          queryKey: installedProductsKeys.detailById(
            variables.turbineId,
            token
          ),
          exact: true
        });

        queryClient.invalidateQueries({
          queryKey: turbinesKey.turbineDetail(token, variables.turbineId),
          exact: true
        });
      }
    }
  });
};

export { useGetTurbineList as default };

interface UseGetHistoryInstalledProductDataOptionsProps {
  turbineType: string;
  turbineSerialNumber: string;
  useQueryOptions?: Record<string, unknown>;
}

export const useGetHistoryInstalledProductDataOptions = ({
  turbineType,
  turbineSerialNumber,
  useQueryOptions = {}
}: UseGetHistoryInstalledProductDataOptionsProps) => {
  const token = useSelector(
    (state: { auth?: { accessToken?: string } }) => state?.auth?.accessToken
  );
  useShouldRefresh();

  return useQuery({
    queryKey: installedProductsKeys.listByFilters(
      { turbineType, turbineSerialNumber },
      token
    ),
    queryFn: async () => {
      const res = await getHistoricalDataOptions({
        token,
        turbineType,
        turbineSerialNumber
      });
      return res.data;
    },
    enabled: true,
    meta: { errorDisplay: 'toast' as const },
    ...useQueryOptions
  });
};

interface UseGetHistoryTagsProps {
  turbineSerialNumber: string;
  useQueryOptions?: Record<string, unknown>;
}

export const useGetHistoryTags = ({
  turbineSerialNumber,
  useQueryOptions = {}
}: UseGetHistoryTagsProps) => {
  const token = useSelector(
    (state: { auth?: { accessToken?: string } }) => state?.auth?.accessToken
  );
  useShouldRefresh();

  return useQuery({
    queryKey: installedProductsKeys.tags(token, turbineSerialNumber),
    queryFn: async () => {
      const res = await getHistoryTags({ token, turbineSerialNumber });
      return res.data;
    },
    enabled: true,
    ...useQueryOptions
  });
};

interface UseExportSummaryHistoryProps {
  params: {
    turbineId: string;
    startDate: string;
    endDate: string;
    interval: string;
    applyDateFormat: boolean;
  };
  body: { metrics: TurbineHistorySeriesMetric[] };
  useQueryOptions?: Record<string, unknown>;
}

export const useExportSummaryHistory = ({
  params,
  body,
  useQueryOptions = {}
}: UseExportSummaryHistoryProps) => {
  const token = useSelector(
    (state: { auth?: { accessToken?: string } }) => state?.auth?.accessToken
  );
  useShouldRefresh();

  return useQuery({
    queryKey: installedProductsKeys.listByFilters(
      { ...params, ...body },
      token
    ),
    queryFn: async () => {
      const res = await exportSummaryHistory({ token, params, body });
      return res.data;
    },
    enabled: false,
    ...useQueryOptions
  });
};
